// src/components/fix-actions/redesign/action-items-shell.tsx
//
// Redesign shell: DC module header + tab-aware agent thread + tab navigation.
// It is driven entirely by the page's existing state and handlers, so all
// orchestration (services, events, tracker) stays in fix-actions-page.tsx.
//
// For this checkpoint the Suggested Actions tab is fully redesigned; the
// Studio and Log tabs render the legacy components passed in as slots, so the
// module stays fully functional while those tabs are rebuilt.

import { useEffect, useMemo, useState } from "react";
import type { FixAction } from "../../../services/fix-actions/fix-actions-service";
import { Ic, SPARKLE } from "./shared";
import { AgentThread, type AgentThreadModel } from "./agent-thread";
import { SuggestedActionsView } from "./suggested-actions-view";
import { OptimizationStudioView } from "./optimization-studio-view";
import type { DetailCallbacks } from "./action-detail-panel";
import type { StrategyArtifact } from "../../../services/fix-actions/optimization-studio-service";
import "./action-items-redesign.css";

type Layout = "split" | "accordion";

export interface ActionItemsShellProps {
  brandName: string;
  activeTab: string;
  onTabChange: (t: string) => void;

  highPriorityCount: number;
  quickWinsCount: number;
  completedCount: number;

  actions: FixAction[];
  filterCategory: string;
  onFilterChange: (v: string) => void;
  searchQuery: string;
  onSearchChange: (v: string) => void;
  onGenerateAsset: DetailCallbacks["onGenerateAsset"];
  onExplainWithAI: (topic: string) => void;
  onNavigateToStudio?: (artifactId: string) => void;
  highlightActionTitle?: string | null;

  // Optimization Studio (redesigned) props
  onOpenArtifactTray: (artifact: StrategyArtifact) => void;
  optimisticArtifacts: StrategyArtifact[];
  brandId: string;
  highlightStudioArtifactId?: string | null;
  onClearStudioHighlight?: () => void;

  logSlot: React.ReactNode;
}

const TABS = [
  { k: "actions", label: "Suggested Actions" },
  { k: "content", label: "Optimization Studio" },
  { k: "insights", label: "Action Log" },
];

export function ActionItemsShell(props: ActionItemsShellProps) {
  const [ready, setReady] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<number | null>(props.actions[0]?.id ?? null);
  const [layout, setLayout] = useState<Layout>("split");

  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  // ── move-mode agent thread from real data (no fabricated claims) ──
  const moveAction: FixAction | null = useMemo(() => {
    if (props.actions.length === 0) return null;
    const quick = props.actions
      .filter((a) => a.effort === "Low")
      .sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0));
    if (quick[0]) return quick[0];
    return [...props.actions].sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0))[0];
  }, [props.actions]);

  const thenActions = useMemo(() => {
    if (!moveAction) return [];
    return [...props.actions]
      .filter((a) => a.id !== moveAction.id)
      .sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0))
      .slice(0, 2);
  }, [props.actions, moveAction]);

  const selectAction = (id: number) => {
    setSelectedActionId(id);
    setLayout("split");
    if (props.activeTab !== "actions") props.onTabChange("actions");
  };

  const agentModel: AgentThreadModel | null = useMemo(() => {
    if (!moveAction) return null;
    const n = moveAction.artifacts?.length ?? 0;
    return {
      mode: "move",
      status: `Reviewed ${props.actions.length} action${props.actions.length === 1 ? "" : "s"}`,
      lead: `${props.highPriorityCount} high-priority action${props.highPriorityCount === 1 ? "" : "s"} and ${props.quickWinsCount} quick win${props.quickWinsCount === 1 ? "" : "s"} are on your roadmap.`,
      rest: "Start with the highest-leverage move.",
      moveLabel: "Starting here",
      moveMeta: `${moveAction.strategicTheme || moveAction.category} · ${n} asset${n === 1 ? "" : "s"}`,
      moveTitle: moveAction.title,
      moveRationale: `${moveAction.effort} effort, ${moveAction.impactLevel} impact. I'll draft ${n} asset${n === 1 ? "" : "s"} for this action.`,
      moveCta: n > 0 ? `Draft ${n} asset${n === 1 ? "" : "s"}` : "Review action",
      onRunMove: () => {
        if (n > 0) {
          const first = moveAction.artifacts![0];
          props.onGenerateAsset(moveAction.id, moveAction.title, moveAction.strategicTheme || "", first.title, first.type, first.description || "", 0, n);
        } else {
          selectAction(moveAction.id);
        }
      },
      thenVerb: "Then I'll move to",
      thenChips: thenActions.map((a) => ({
        label: a.strategicTheme || a.title,
        onSelect: () => selectAction(a.id),
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveAction, thenActions, props.actions.length, props.highPriorityCount, props.quickWinsCount]);

  const tabBtn = (active: boolean): React.CSSProperties => ({
    position: "relative",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "14px 0",
    fontSize: 16,
    fontWeight: active ? 600 : 500,
    color: active ? "var(--foreground)" : "var(--muted-foreground)",
  });

  return (
    <div className="ai-redesign" style={{ minHeight: "100vh", background: "var(--background)", color: "var(--foreground)", fontFamily: "var(--font-sans)", fontSize: 14, lineHeight: 1.5 }}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* module header */}
        <header style={{ padding: "26px clamp(16px,2vw,24px) 22px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 16, fontSize: 13 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--muted-foreground)", fontWeight: 500 }}>
              <Ic paths={["M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4Z"]} size={15} sw={1.7} />
              Optimize
            </span>
            <span style={{ color: "var(--silver-mist)" }}>/</span>
            <span style={{ color: "var(--foreground)", fontWeight: 600 }}>Action Items</span>
          </div>
          <h1 style={{ fontSize: "clamp(24px,3.5vw,30px)", fontWeight: 600, letterSpacing: "-.02em", lineHeight: 1.25, color: "var(--foreground)", margin: "0 0 8px" }}>
            Prioritize optimization actions. Automate assets.
          </h1>
          <p style={{ fontSize: 15, color: "var(--muted-foreground)", margin: "0 0 18px", maxWidth: 760 }}>
            AI-prioritized optimization roadmap based on your perception baseline. Start with high-impact actions, deploy with confidence, then measure improvement.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--muted-foreground)", fontWeight: 500 }}>Action Status</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: "var(--infrared-coral)", flexShrink: 0 }} />
              <strong style={{ fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{props.highPriorityCount}</strong>
              <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>high priority</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Ic paths={SPARKLE} size={14} sw={1.8} color="var(--alpha-teal)" />
              <strong style={{ fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{props.quickWinsCount}</strong>
              <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>quick wins</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Ic paths={["M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z", "m8.5 12 2.5 2.5L16 9"]} size={14} sw={1.9} color="var(--signal-green)" />
              <strong style={{ fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{props.completedCount}</strong>
              <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>completed</span>
            </div>
          </div>
        </header>

        {/* agent thread - actions tab only for this checkpoint */}
        {props.activeTab === "actions" && agentModel && <AgentThread model={agentModel} />}

        {/* tab nav */}
        <div style={{ marginTop: 14, padding: "0 clamp(16px,2vw,24px)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 28 }}>
          {TABS.map((t) => {
            const active = props.activeTab === t.k;
            return (
              <button key={t.k} onClick={() => props.onTabChange(t.k)} style={tabBtn(active)}>
                {t.label}
                {active && <span style={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: "var(--alpha-teal)", borderRadius: "2px 2px 0 0" }} />}
              </button>
            );
          })}
        </div>

        {/* body */}
        {props.activeTab === "actions" && (
          <SuggestedActionsView
            actions={props.actions}
            filterCategory={props.filterCategory}
            onFilterChange={props.onFilterChange}
            searchQuery={props.searchQuery}
            onSearchChange={props.onSearchChange}
            onGenerateAsset={props.onGenerateAsset}
            onExplainWithAI={props.onExplainWithAI}
            onNavigateToStudio={props.onNavigateToStudio}
            highlightActionTitle={props.highlightActionTitle}
            ready={ready}
            selectedId={selectedActionId}
            onSelectAction={setSelectedActionId}
            layout={layout}
            onLayoutChange={setLayout}
          />
        )}
        {props.activeTab === "content" && (
          <div style={{ padding: "8px clamp(16px,2vw,24px) 60px" }}>
            <OptimizationStudioView
              onOpenArtifactTray={props.onOpenArtifactTray}
              onExplainWithAI={props.onExplainWithAI}
              optimisticArtifacts={props.optimisticArtifacts}
              brandId={props.brandId}
              highlightArtifactId={props.highlightStudioArtifactId}
              onClearHighlight={props.onClearStudioHighlight}
            />
          </div>
        )}
        {props.activeTab === "insights" && <div style={{ padding: "8px clamp(16px,2vw,24px) 60px" }}>{props.logSlot}</div>}
      </div>
    </div>
  );
}
